import { cn, Tooltip } from '@heroui/react';

import {
  DEPLOYMENT_ISSUE_ICON,
  DeploymentIssue,
  DeploymentIssueSeverity,
  DeploymentIssueSummary
} from './deploymentIssue';

export type DeploymentIssueIndicatorProps = {
  summary: DeploymentIssueSummary | null;
  className?: string;
  /** Overridable, so a screen holding more than one indicator can tell them apart. */
  'data-testid'?: string;
};

/** An error is red, a warning is amber — the same severity the drawer alerts carry. */
const SEVERITY_COLOR: Record<DeploymentIssueSeverity, string> = {
  danger: 'text-on-negative-container',
  warning: 'text-on-warning-container'
};

/** One problem: its severity icon, the short label, the whole sentence under it. */
const IssueRow = ({ issue }: { issue: DeploymentIssue }) => {
  const Icon = DEPLOYMENT_ISSUE_ICON[issue.severity];

  return (
    <div className='flex w-full items-start gap-1.5'>
      <Icon
        className={cn('icon-smallest shrink-0', SEVERITY_COLOR[issue.severity])}
        aria-hidden
      />
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <p className='text-label-md text-on-surface'>{issue.label}</p>
        <p className='text-body-regular-sm text-on-surface-variant'>
          {issue.description}
        </p>
      </div>
    </div>
  );
};

/**
 * Flags a deployment problem in the Run listing, so the user does not have to open the details
 * sidesheet to find out that something needs attention — an edited Global or Account waiting for
 * a redeploy, out of memory, a degraded deployment.
 *
 * One indicator, never a row of them: it takes the worst severity, counts the rest, and leaves the
 * breakdown to the tooltip, which lists every problem as label plus sentence. Exposed as a labelled
 * image whose accessible name carries the same text, so nothing here is hover-only.
 */
export const DeploymentIssueIndicator = ({
  summary,
  className,
  'data-testid': dataTestId = 'deployment-issue-indicator'
}: DeploymentIssueIndicatorProps) => {
  if (!summary) {
    return null;
  }

  const Icon = DEPLOYMENT_ISSUE_ICON[summary.severity];

  return (
    <Tooltip
      content={
        <div className='flex flex-col gap-2.5'>
          {summary.issues.map(issue => (
            <IssueRow key={issue.kind} issue={issue} />
          ))}
        </div>
      }
      placement='bottom'
      classNames={{
        content: 'max-w-[317px] rounded-small bg-surface p-2 shadow-small'
      }}
      offset={4}
    >
      <span
        role='img'
        aria-label={summary.description}
        data-testid={dataTestId}
        className={cn(
          'flex items-center gap-0.5 px-0.5',
          SEVERITY_COLOR[summary.severity],
          className
        )}
      >
        <Icon className='icon-smallest' aria-hidden />
        {/* Counted even at one, as the design has it: the number is what says "how much". */}
        <span className='font-mono text-label-md tracking-wider'>
          {summary.count}
        </span>
      </span>
    </Tooltip>
  );
};

export default DeploymentIssueIndicator;
